// Minimal OpenAI Chat Completions client (streaming + tool calls), called straight from the
// visitor's browser with the visitor's own API key. There is no server in between: OpenAI's API
// allows cross-origin requests. Works with any OpenAI-compatible endpoint via settings.baseUrl.

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

/** Splits a text/event-stream into `data:` payloads. */
export function createSseParser(onData) {
  let buffer = '';
  return {
    push(chunk) {
      buffer += chunk;
      let m;
      while ((m = /\r?\n\r?\n/.exec(buffer))) {
        const block = buffer.slice(0, m.index);
        buffer = buffer.slice(m.index + m[0].length);
        const data = block
          .split(/\r?\n/)
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).replace(/^ /, ''))
          .join('\n');
        if (data) onData(data);
      }
    },
    flush() {
      if (buffer.trim()) this.push('\n\n');
    },
  };
}

/** Folds one streamed `choices[0].delta` into the accumulated message. */
export function applyDelta(acc, delta) {
  if (!delta) return acc;
  if (typeof delta.content === 'string') acc.content += delta.content;
  for (const tc of delta.tool_calls ?? []) {
    const i = tc.index ?? acc.toolCalls.length;
    const cur = (acc.toolCalls[i] ??= { id: '', name: '', arguments: '' });
    if (tc.id) cur.id = tc.id;
    if (tc.function?.name) cur.name += tc.function.name;
    if (tc.function?.arguments) cur.arguments += tc.function.arguments;
  }
  return acc;
}

async function errorFrom(res) {
  let detail = '';
  try {
    const j = await res.json();
    detail = j?.error?.message ?? JSON.stringify(j);
  } catch {
    detail = await res.text().catch(() => '');
  }
  const hint =
    res.status === 401 ? ' Check the API key in Settings.' : res.status === 404 ? ' Check the model name in Settings.' : res.status === 429 ? ' Rate limit or quota reached.' : '';
  return new ApiError(`OpenAI ${res.status}: ${detail || res.statusText}${hint}`, res.status);
}

/** Models that rejected reasoning_effort alongside tools this session. */
const noReasoningWithTools = new Set();

/** Only Authorization and Content-Type: the headers OpenAI's CORS policy allows. */
function headers(settings, json = true) {
  const h = { Authorization: `Bearer ${settings.apiKey}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/**
 * One streamed completion. Calls onText(delta) as text arrives.
 * Resolves { content, toolCalls: [{ id, name, arguments }], finishReason }.
 */
export async function streamChat({ settings, messages, tools, signal, onText }) {
  const body = { model: settings.model, messages, stream: true };
  if (tools?.length) {
    body.tools = tools;
    body.tool_choice = 'auto';
  }
  if (settings.reasoningEffort && !noReasoningWithTools.has(settings.model)) body.reasoning_effort = settings.reasoningEffort;

  const post = () => fetch(`${settings.baseUrl}/chat/completions`, { method: 'POST', headers: headers(settings), body: JSON.stringify(body), signal });
  let res = await post();
  if (!res.ok && res.status === 400 && body.reasoning_effort) {
    // Some models (e.g. gpt-5.4-nano) refuse reasoning_effort together with tools on
    // this endpoint. Retry without it and remember, rather than failing the chat.
    const err = await errorFrom(res);
    if (!/reasoning_effort/i.test(err.message)) throw err;
    noReasoningWithTools.add(settings.model);
    delete body.reasoning_effort;
    res = await post();
  }
  if (!res.ok) throw await errorFrom(res);

  const acc = { content: '', toolCalls: [], finishReason: null };
  const parser = createSseParser((data) => {
    if (data === '[DONE]') return;
    let json;
    try {
      json = JSON.parse(data);
    } catch {
      return;
    }
    if (json.error) throw new ApiError(`OpenAI: ${json.error.message ?? JSON.stringify(json.error)}`);
    const choice = json.choices?.[0];
    if (!choice) return;
    const before = acc.content.length;
    applyDelta(acc, choice.delta);
    if (acc.content.length > before) onText?.(acc.content.slice(before));
    if (choice.finish_reason) acc.finishReason = choice.finish_reason;
  });

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(decoder.decode(value, { stream: true }));
  }
  parser.flush();
  acc.toolCalls = acc.toolCalls.filter(Boolean);
  return acc;
}

/** Transcription models that take `languages[]` and `keywords[]` instead of a single `language`. */
const contextualStt = (model) => /^gpt-(live-)?transcribe/.test(model);

/**
 * Speech to text (POST /audio/transcriptions). `audio` is a Blob; `keywords` are terms
 * the speaker may use (sent only to models that accept them). Resolves the transcript.
 */
export async function transcribe({ settings, audio, filename = 'speech.webm', keywords = [], signal }) {
  const model = settings.sttModel;
  const languages = String(settings.sttLanguage ?? '')
    .split(/[\s,]+/)
    .filter(Boolean);
  const form = new FormData();
  form.append('file', audio, filename);
  form.append('model', model);
  if (contextualStt(model)) {
    for (const l of languages) form.append('languages[]', l);
    for (const k of keywords) if (k && !/[<>\r\n]/.test(k)) form.append('keywords[]', k);
  } else if (languages[0]) form.append('language', languages[0]);
  const res = await fetch(`${settings.baseUrl}/audio/transcriptions`, { method: 'POST', headers: headers(settings, false), body: form, signal });
  if (!res.ok) throw await errorFrom(res);
  const j = await res.json();
  return String(j.text ?? '').trim();
}

/** Text to speech (POST /audio/speech). Resolves the audio as a Blob. */
export async function synthesize({ settings, text, signal }) {
  const body = { model: settings.ttsModel, voice: settings.ttsVoice, input: text, response_format: 'mp3' };
  // tts-1 and tts-1-hd don't take delivery instructions
  if (settings.ttsStyle && !/^tts-1/.test(settings.ttsModel)) body.instructions = settings.ttsStyle;
  const res = await fetch(`${settings.baseUrl}/audio/speech`, { method: 'POST', headers: headers(settings), body: JSON.stringify(body), signal });
  if (!res.ok) throw await errorFrom(res);
  return res.blob();
}

/** Model ids available to this key (used to fill the model picker). */
export async function listModels(settings) {
  const res = await fetch(`${settings.baseUrl}/models`, { headers: headers(settings, false) });
  if (!res.ok) throw await errorFrom(res);
  const j = await res.json();
  return (j.data ?? []).map((m) => m.id).sort();
}
